import {
    ExistingControlResult,
    FutureControlResult,
    ExistingControlResultDtoContent,
    FutureControlResultDtoContent,
    CategoryAndTag,
} from "api/generated/model";
import { matchesCategoryAndTagFilterFn } from "utils/tags/matchesCategoryAndTagFilterFn";

import { DashboardFilterType, ExtractedTagCategory } from "../types";

const FILTER_TYPES: DashboardFilterType[] = [
    "ControlPrograms",
    "Tags",
    "Entities",
];

export function getEnabledFilterTypes(
    controlEntitiesEnabled: boolean
): DashboardFilterType[] {
    return FILTER_TYPES.filter(
        (filterType) => filterType !== "Entities" || controlEntitiesEnabled
    );
}

// Data transformation utilities
export function extractTagCategoriesFromControlResults(
    controlResults: ExistingControlResultDtoContent[],
    futureControlResults: FutureControlResultDtoContent[]
): ExtractedTagCategory[] {
    const tagsPerRow: Record<string, string[]>[] = [
        ...controlResults,
        ...futureControlResults,
    ].map((controlResult) => controlResult.resource.tags);
    return buildExtractedTagCategories(tagsPerRow);
}

export function buildExtractedTagCategories(
    tagsPerRow: Record<string, string[]>[]
): ExtractedTagCategory[] {
    const tagCategoryMap = new Map<string, Set<string>>();
    tagsPerRow.forEach((tags) => {
        Object.entries(tags).forEach(([categoryName, tagNames]) => {
            if (!tagCategoryMap.has(categoryName)) {
                tagCategoryMap.set(categoryName, new Set());
            }
            const tagSet = tagCategoryMap.get(categoryName)!;
            tagNames.forEach((tagName) => tagSet.add(tagName));
        });
    });
    return Array.from(tagCategoryMap.entries())
        .map(([categoryName, tagNamesSet]) => ({
            name: categoryName,
            tags: Array.from(tagNamesSet).sort(),
        }))
        .sort((a, b) => a.name.localeCompare(b.name));
}

export function applyTagFiltering<
    T extends { resource: ExistingControlResult | FutureControlResult },
>(controlResults: T[], tagSelections: CategoryAndTag[]): T[] {
    if (tagSelections.length === 0) {
        return controlResults;
    }
    return controlResults.filter((controlResult) =>
        matchesCategoryAndTagFilterFn(
            controlResult.resource.tags,
            tagSelections
        )
    );
}

export function applyEntityFiltering<
    T extends { resource: ExistingControlResult | FutureControlResult },
>(controlResults: T[], entityIds: number[]): T[] {
    if (entityIds.length === 0) {
        return controlResults;
    }
    const entityIdSet: Set<number> = new Set(entityIds);
    return controlResults.filter((controlResult) => {
        const entityId = getEntityId(controlResult.resource);
        return entityId !== null && entityIdSet.has(entityId);
    });
}

/** `ExistingControlResult` carries a full entity snapshot, `FutureControlResult` only its id. */
function getEntityId(
    resource: ExistingControlResult | FutureControlResult
): number | null {
    if ("entity" in resource) {
        return resource.entity?.id ?? null;
    }
    return "entityId" in resource ? (resource.entityId ?? null) : null;
}

export function deduplicateArrayByKeyCallback<T, K>(getKey: (value: T) => K) {
    const keys: Set<K> = new Set();
    return (val: T) => {
        const key = getKey(val);
        if (!keys.has(key)) {
            keys.add(key);
            return true;
        } else {
            return false;
        }
    };
}
